<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { useQaDrawer } from 'dashboard/composables/useQaDrawer';
import Avatar from 'next/avatar/Avatar.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import LineChart from 'shared/components/charts/LineChart.vue';
import QaDateRangeSelect from '../components/QaDateRangeSelect.vue';
import { registerQaChartPlugins } from '../helper/chartPlugins';
import {
  COLUMN_GROUPS,
  COLUMN_BY_KEY,
  DEFAULT_VISIBLE,
  cellDelta,
  cellRatio,
  cellTone,
  cellValue,
  isPickable,
  pickableColumns,
  resolveVisibleColumns,
} from '../helper/agentKpiColumns';
import QaDashboardApi from 'dashboard/api/qaDashboard';
import '../styles/qaShell.css';

// Stable compile marker for release verification.
const QA_AGENT_KPI_DEEPEN_V1 = 'QA_AGENT_KPI_DEEPEN_V1';

// The threshold line and value labels live in helper/chartPlugins.js so the
// other QA charts can use them, and so they can be tested without a page.
registerQaChartPlugins();

const { t } = useI18n();
const showAlert = useAlert;
const route = useRoute();
const store = useStore();
const { accountScopedRoute } = useAccount();

const isLoading = ref(false);
const snapshotHash = ref('');
const banner = ref('');
const calibrationOnly = ref(true);
const definition = reactive({});
const factHealth = reactive({
  status: 'unknown',
  rates_available: false,
  metrics: {},
  freshness: {},
  coverage: {},
  missing: {},
});
const teamMetrics = reactive({
  first_human_response: {},
  ongoing_response: {},
  unassigned_queue: {},
  quality: {},
  attribution: {},
});
const agentRows = ref([]);
// Same metrics for the equal-length window immediately before this one, so every
// headline number can be read as a movement instead of a bare figure.
const previousTeam = ref(null);
const selectedAgentId = ref('');
const selectedChartMetric = ref('first_human_response');
const languageFilter = ref('');
const teamFilter = ref('');
const inboxFilter = ref('');
const channelFilter = ref('');
const shiftFilter = ref('');
const selectedDetailId = ref(null);
const dataSource = ref('live');
const lastUpdated = ref(null);
const lastUpdatedText = computed(() => {
  if (!lastUpdated.value) return '';
  const mins = Math.max(
    0,
    Math.round((Date.now() - new Date(lastUpdated.value).getTime()) / 60000)
  );
  return t('QA_DASHBOARD.AGENT_KPI.LAST_UPDATED', { minutes: mins });
});
const compileMarker = computed(() => QA_AGENT_KPI_DEEPEN_V1);

const toDateInput = date => {
  const d = new Date(date);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

// Guardrail: the date picker cannot reach further back than MAX_WINDOW_DAYS.
// Keeps every request within the pre-computed range so a wide historical pick
// can never trigger an unbounded live compute (backend caps this too).
const MAX_WINDOW_DAYS = 90;
const DAY_MS = 24 * 60 * 60 * 1000;
const minDateInput = computed(() =>
  toDateInput(Date.now() - MAX_WINDOW_DAYS * DAY_MS)
);
const todayInput = computed(() => anchorToday());
const windowCapped = ref(false);

// The day the account's reports are bucketed by. Bootstrapped from the account
// record so the first request is already right, then confirmed by the response,
// which knows the QA reporting timezone rather than just the account one.
const reportingToday = ref('');

const todayInZone = zone => {
  if (!zone) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: zone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).format(new Date());
  } catch (error) {
    return '';
  }
};

// A rate built on a handful of replies is noise wearing a percentage sign: one
// late reply out of one reads as 100% overdue next to a genuine 100% out of
// forty. Rows under this many replies are listed but not scored.
const DEFAULT_MIN_SAMPLE = 10;
const DEFAULT_TARGET_RATE = 0.9;
const minSample = computed(
  () => Number(definition.min_sample_size) || DEFAULT_MIN_SAMPLE
);
const targetRate = computed(
  () => Number(definition.first_response_target_rate) || DEFAULT_TARGET_RATE
);

// Whole days, defaulting to the last week. A single-day default made the normal
// case an empty page: a quiet today looks identical to a broken pipeline.
const PRESET_DAYS = { today: 1, week: 7, month: 30 };
const startForDays = days => toDateInput(Date.now() - (days - 1) * DAY_MS);

const anchorToday = () => reportingToday.value || toDateInput(Date.now());

const startForDaysFrom = days => {
  const anchor = new Date(`${anchorToday()}T00:00:00`);
  const base = Number.isNaN(anchor.getTime()) ? Date.now() : anchor.getTime();
  return toDateInput(base - (days - 1) * DAY_MS);
};

const filters = reactive({
  window_start: startForDays(PRESET_DAYS.week),
  window_end: toDateInput(Date.now()),
});

const windowDays = computed(() => {
  const start = new Date(`${filters.window_start}T00:00:00`);
  const end = new Date(`${filters.window_end}T00:00:00`);
  const days = Math.round((end - start) / DAY_MS) + 1;
  return Number.isFinite(days) && days > 0 ? days : 1;
});

const activePreset = computed(() => {
  if (filters.window_end !== todayInput.value) return '';
  const match = Object.entries(PRESET_DAYS).find(
    ([, days]) => filters.window_start === startForDays(days)
  );
  return match ? match[0] : '';
});

const agents = computed(() => store.getters['agents/getAgents'] || []);
const teams = computed(() => store.getters['teams/getTeams'] || []);
const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const channelOptions = computed(() => {
  const set = new Set();
  inboxes.value.forEach(inbox => {
    if (inbox.channel_type)
      set.add(
        String(inbox.channel_type)
          .replace(/^Channel::/, '')
          .toLowerCase()
      );
  });
  return Array.from(set).sort();
});

const agentById = id =>
  agents.value.find(a => Number(a.id) === Number(id)) || null;

const agentName = id => {
  const agent = agentById(id);
  if (!agent) return `#${id}`;
  return agent.name || agent.available_name || agent.email || `#${id}`;
};

const agentThumbnail = id => agentById(id)?.thumbnail || '';

// Shown under the name: two colleagues called "Fai" are indistinguishable in a
// table that judges their work.
const agentEmail = id => {
  const agent = agentById(id);
  return agent?.email && agent.email !== agent.name ? agent.email : '';
};

const pct = value => {
  if (value === null || value === undefined || value === '') return '—';
  return `${(Number(value) * 100).toFixed(1)}%`;
};

const num = value => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value);
};

const fmtSeconds = value => {
  if (value === null || value === undefined || value === '') return '—';
  const n = Number(value);
  if (Number.isNaN(n)) return '—';
  if (n < 60) return `${n.toFixed(1)}s`;
  return `${(n / 60).toFixed(1)}m`;
};

const RESPONSE_METRICS = [
  'first_human_response',
  'ongoing_response',
  'unassigned_queue',
];

const metricEntry = key => factHealth.metrics?.[key] || null;

// A payload from before per-metric verdicts existed has no metrics block; fall
// back to the single flag so a rolling deploy never blanks the whole page.
const metricAvailable = key => {
  const entry = metricEntry(key);
  if (!entry) return Boolean(factHealth.rates_available);
  return entry.available === true;
};

const metricPartial = key => metricEntry(key)?.partial === true;

const metricReasonText = key => {
  const reason = metricEntry(key)?.reason || 'unknown';
  return t(
    `QA_DASHBOARD.AGENT_KPI.FACT_REASON_${String(reason).toUpperCase()}`
  );
};

const gate = (key, formatter, value) =>
  metricAvailable(key) ? formatter(value) : '—';

const frtPct = value => gate('first_human_response', pct, value);
const frtNum = value => gate('first_human_response', num, value);
const frtSeconds = value => gate('first_human_response', fmtSeconds, value);
const claimNum = value => gate('claim_latency', num, value);
const ongoingNum = value => gate('ongoing_response', num, value);
const queueNum = value => gate('unassigned_queue', num, value);

// sample_size sums FRT episodes and ongoing turns, so it survives either source.
const sampleNum = value =>
  metricAvailable('first_human_response') || metricAvailable('ongoing_response')
    ? num(value)
    : '—';

const anyPartial = computed(() => RESPONSE_METRICS.some(metricPartial));
const allWithheld = computed(
  () => !RESPONSE_METRICS.some(key => metricAvailable(key))
);
const factComplete = computed(
  () => RESPONSE_METRICS.every(metricAvailable) && !anyPartial.value
);

const rateBarWidth = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(100, n * 100));
};

const rateTone = value => {
  const n = Number(value);
  if (!Number.isFinite(n)) return 'qa-progress--warn';
  if (n >= targetRate.value) return 'qa-progress--good';
  if (n >= 0.7) return 'qa-progress--warn';
  return 'qa-progress--bad';
};

const factStatusLabel = computed(() => {
  const status = String(factHealth.status || 'unknown').toUpperCase();
  return t(`QA_DASHBOARD.AGENT_KPI.FACT_STATUS_${status}`);
});

const factHealthPillClass = computed(() =>
  factComplete.value ? 'qa-pill qa-pill--good' : 'qa-pill qa-pill--warn'
);

const languageOptions = computed(() => {
  const set = new Set();
  agentRows.value.forEach(row => {
    if (row.language_hint) set.add(row.language_hint);
  });
  return Array.from(set).sort();
});

const visibleRows = computed(() => {
  let rows = agentRows.value;
  if (selectedAgentId.value) {
    rows = rows.filter(
      r => Number(r.agent_user_id) === Number(selectedAgentId.value)
    );
  }
  if (languageFilter.value) {
    rows = rows.filter(r => r.language_hint === languageFilter.value);
  }
  return rows;
});

// --- scoring -----------------------------------------------------------------

const rowRate = row => {
  const rate = row?.first_human_response?.rate;
  return rate === null || rate === undefined ? null : Number(rate);
};

const rowSample = row => Number(row?.first_human_response?.eligible_count) || 0;

const rowScored = row =>
  metricAvailable('first_human_response') &&
  rowRate(row) !== null &&
  rowSample(row) >= minSample.value;

// Median over scored rows only, for the same reason the rows themselves are
// gated: a two-reply agent should not drag the line everyone is compared to.
const teamMedianRate = computed(() => {
  const rates = visibleRows.value
    .filter(rowScored)
    .map(rowRate)
    .sort((a, b) => a - b);
  if (!rates.length) return null;
  const mid = Math.floor(rates.length / 2);
  return rates.length % 2 ? rates[mid] : (rates[mid - 1] + rates[mid]) / 2;
});

const vsMedian = row => {
  if (!rowScored(row) || teamMedianRate.value === null) return null;
  return (rowRate(row) - teamMedianRate.value) * 100;
};

const vsMedianText = row => {
  const diff = vsMedian(row);
  if (diff === null) return '';
  if (Math.abs(diff) < 0.05) return t('QA_DASHBOARD.AGENT_KPI.VS_MEDIAN_AT');
  return t('QA_DASHBOARD.AGENT_KPI.VS_MEDIAN', {
    arrow: diff > 0 ? '▲' : '▼',
    points: Math.abs(diff).toFixed(1),
  });
};

const vsMedianClass = row => {
  const diff = vsMedian(row);
  if (diff === null || Math.abs(diff) < 0.05) return 'qa-vs';
  return diff > 0 ? 'qa-vs qa-vs--up' : 'qa-vs qa-vs--down';
};

const rowSeverity = row => {
  // A confirmed unsafe message outranks every timing figure and is judged on
  // its own, before the min-sample gate: one is already too many, and waiting
  // for ten replies' worth of evidence to say so would be absurd.
  if (Number(row?.safety?.confirmed_violations) > 0) return 'bad';
  if (!rowScored(row)) return 'mute';
  const rate = rowRate(row);
  if (rate < 0.7) return 'bad';
  if (rate < targetRate.value) return 'warn';
  if (Number(row?.ongoing_response?.final_unanswered) > 0) return 'warn';
  return 'good';
};

// ── which columns the table shows ───────────────────────────────────────────
// Scoped per account, like the Agent performance report's own picker: one team
// works nights and cares about claim latency, another does not, and neither
// should have to re-tick the list every visit.
const COLUMN_STORAGE_KEY = 'wlab.qa-agent-kpi.columns.v1';
const columnStorageKey = computed(
  () => `${COLUMN_STORAGE_KEY}.${route.params?.accountId || 'global'}`
);

const selectedColumnKeys = ref([...DEFAULT_VISIBLE]);
const columnsMenuOpen = ref(false);

const readStoredColumns = () => {
  try {
    const raw = window.localStorage.getItem(columnStorageKey.value);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Keys are dropped rather than trusted: a column removed in a later release
    // must not resurrect itself from an old browser's storage.
    return Array.isArray(parsed)
      ? parsed.filter(key => COLUMN_BY_KEY[key])
      : null;
  } catch (error) {
    return null;
  }
};

const persistColumns = () => {
  try {
    window.localStorage.setItem(
      columnStorageKey.value,
      JSON.stringify(selectedColumnKeys.value)
    );
  } catch (error) {
    // A private-mode browser refusing storage is not worth an error to the user.
  }
};

// A window of exactly one day is what makes percentiles honest; see
// agentKpiColumns for why they cannot be merged across days.
const isSingleDayWindow = computed(
  () => filters.window_start === filters.window_end
);

const tableColumns = computed(() =>
  resolveVisibleColumns({
    selected: selectedColumnKeys.value,
    singleDay: isSingleDayWindow.value,
    hasLanguage: languageColumnVisible.value,
  })
);

const columnMenuGroups = computed(() => {
  const offered = pickableColumns({ singleDay: isSingleDayWindow.value });
  return COLUMN_GROUPS.map(group => ({
    ...group,
    label: t(`QA_DASHBOARD.AGENT_KPI.${group.labelKey}`),
    columns: offered.filter(col => col.group === group.id),
    locked: Object.values(COLUMN_BY_KEY).filter(
      col => col.group === group.id && !isPickable(col)
    ),
  })).filter(group => group.columns.length || group.locked.length);
});

const isColumnOn = key => selectedColumnKeys.value.includes(key);

const toggleColumn = key => {
  selectedColumnKeys.value = isColumnOn(key)
    ? selectedColumnKeys.value.filter(k => k !== key)
    : [...selectedColumnKeys.value, key];
  persistColumns();
};

const toggleColumnGroup = group => {
  const keys = group.columns.map(col => col.key);
  const allOn = keys.every(isColumnOn);
  const next = new Set(selectedColumnKeys.value);
  keys.forEach(key => (allOn ? next.delete(key) : next.add(key)));
  selectedColumnKeys.value = [...next];
  persistColumns();
};

const resetColumns = () => {
  selectedColumnKeys.value = [...DEFAULT_VISIBLE];
  persistColumns();
};

const showAllColumns = () => {
  selectedColumnKeys.value = Object.values(COLUMN_BY_KEY)
    .filter(isPickable)
    .map(col => col.key);
  persistColumns();
};

// ── one cell ────────────────────────────────────────────────────────────────
const columnCtx = computed(() => ({
  scored: rowScored,
  targetRate: targetRate.value,
  overdueCeiling: OVERDUE_TARGET_RATE,
}));

const previousRows = computed(() => {
  const rows = previousTeam.value?.agents;
  if (!Array.isArray(rows)) return {};
  return rows.reduce((acc, row) => {
    acc[row.agent_user_id] = row;
    return acc;
  }, {});
});

const columnLabel = col => t(`QA_DASHBOARD.AGENT_KPI.${col.labelKey}`);

const cellMain = (col, row) => {
  const value = cellValue(col, row);
  if (value === null) return '—';
  if (col.kind === 'seconds') return fmtSeconds(value);
  if (col.kind === 'text') return value;
  return num(value);
};

// The percentage is context for the count above it, and it is withheld on an
// unscored row for the same reason the colour is: one reply out of one is not
// a hundred per cent of anything worth reporting.
const cellSub = (col, row) => {
  if (col.kind !== 'count') return '';

  // On an unscored row the share is withheld, and the slot says why rather than
  // going blank — "45" with nothing beside it invites the reader to divide it by
  // something themselves.
  if (!rowScored(row)) {
    return col.key === 'onTime'
      ? t('QA_DASHBOARD.AGENT_KPI.SAMPLE_TOO_SMALL', {
          count: num(rowSample(row)),
          min: num(minSample.value),
        })
      : '';
  }

  const ratio = cellRatio(col, row);
  return ratio === null || ratio === undefined ? '' : pct(ratio);
};

const cellMovement = (col, row) =>
  cellDelta(col, row, previousRows.value[row.agent_user_id]);

const cellMovementText = (col, row) => {
  const delta = cellMovement(col, row);
  if (delta === null || delta === 0) return '';
  return `${delta > 0 ? '▲' : '▼'} ${Math.abs(delta)}`;
};

// Direction alone does not say better or worse — more replies on time is good,
// more late replies is not — so each column declares which way is up.
const IMPROVES_WHEN_UP = new Set(['onTime', 'cases', 'needReply']);

const cellMovementClass = (col, row) => {
  const delta = cellMovement(col, row);
  if (delta === null || delta === 0) return '';
  const better = IMPROVES_WHEN_UP.has(col.key) ? delta > 0 : delta < 0;
  return better ? 'qa-move qa-move--good' : 'qa-move qa-move--bad';
};

const cellToneClass = (col, row) => {
  const tone = cellTone(col, row, columnCtx.value);
  return tone ? `qa-cell-tone qa-cell-tone--${tone}` : '';
};

const SEVERITY_ORDER = { bad: 0, warn: 1, good: 2, mute: 3 };

// Worst first: the page is opened to find out who needs help today, not to read
// an alphabetical roster.
const sortedRows = computed(() =>
  [...visibleRows.value].sort((a, b) => {
    const bySeverity =
      SEVERITY_ORDER[rowSeverity(a)] - SEVERITY_ORDER[rowSeverity(b)];
    if (bySeverity !== 0) return bySeverity;
    return (rowRate(a) ?? 1) - (rowRate(b) ?? 1);
  })
);

// Ordered like the table, not like the payload. The chart and the table sit on
// one screen showing the same people; reading worst-first in one and
// arrival-order in the other makes the reader re-find every colleague.
const scoredRows = computed(() => sortedRows.value.filter(rowScored));

const languageColumnVisible = computed(() =>
  visibleRows.value.some(row => row.language_hint)
);

// --- movement vs the previous window ----------------------------------------

// An empty previous window is not a previous value of zero. Number(null) is 0,
// so the first version read "93.1% against nothing" as "up 93 points" — the
// comparison has to prove the earlier window actually measured something, which
// is what `basis` (its denominator) is for.
const deltaFor = (current, previous, { asPoints = false, basis } = {}) => {
  if (!(Number(basis) > 0)) return null;
  if (current === null || current === undefined) return null;
  if (previous === null || previous === undefined) return null;

  const now = Number(current);
  const before = Number(previous);
  if (!Number.isFinite(now) || !Number.isFinite(before)) return null;

  const diff = asPoints ? (now - before) * 100 : now - before;
  return { diff, better: asPoints ? diff > 0 : diff < 0 };
};

const deltaText = delta => {
  if (!delta) return '';
  if (Math.abs(delta.diff) < 0.05)
    return t('QA_DASHBOARD.AGENT_KPI.DELTA_FLAT');
  return t('QA_DASHBOARD.AGENT_KPI.DELTA', {
    arrow: delta.diff > 0 ? '▲' : '▼',
    value: Math.abs(delta.diff).toFixed(Math.abs(delta.diff) < 10 ? 1 : 0),
  });
};

const deltaClass = delta => {
  if (!delta || Math.abs(delta.diff) < 0.05) return 'qa-delta';
  return delta.better ? 'qa-delta qa-delta--up' : 'qa-delta qa-delta--down';
};

const frtDelta = computed(() =>
  previousTeam.value
    ? deltaFor(
        teamMetrics.first_human_response.rate,
        previousTeam.value.first_human_response?.rate,
        {
          asPoints: true,
          basis: previousTeam.value.first_human_response?.eligible_count,
        }
      )
    : null
);
const overdueDelta = computed(() =>
  previousTeam.value
    ? deltaFor(
        teamMetrics.ongoing_response.overdue_turns,
        previousTeam.value.ongoing_response?.overdue_turns,
        { basis: previousTeam.value.ongoing_response?.eligible_turns }
      )
    : null
);
const queueDelta = computed(() =>
  previousTeam.value
    ? deltaFor(
        teamMetrics.unassigned_queue.breach_count,
        previousTeam.value.unassigned_queue?.breach_count,
        { basis: previousTeam.value.unassigned_queue?.count }
      )
    : null
);

// The note must not promise a comparison the cards are not showing. Once the
// deltas learned to stay away from an empty previous window, this line was left
// claiming one anyway.
const hasAnyDelta = computed(() =>
  Boolean(frtDelta.value || overdueDelta.value || queueDelta.value)
);

const comparisonNote = computed(() => {
  if (hasAnyDelta.value) {
    return t('QA_DASHBOARD.AGENT_KPI.COMPARE_NOTE', { days: windowDays.value });
  }
  // Only worth saying once the earlier window has actually been fetched.
  if (previousTeam.value) {
    return t('QA_DASHBOARD.AGENT_KPI.COMPARE_NONE', { days: windowDays.value });
  }
  return '';
});

// Share of ongoing turns allowed to run late. Not in the definition record yet,
// so it lives here and is used by both the card and the comparison chart —
// leaving them free to disagree would be worse.
const OVERDUE_TARGET_RATE = 0.05;
// Mirrors the target printed on the template card; not in the definition record
// yet, so both read the same constant rather than drifting apart.
const TEMPLATE_TARGET_RATE = 0.95;

// The card's basis is a share ("90.9% of 22 turns") so its target has to be a
// share too. It read "Target 180s" — the threshold a single turn is measured
// against — which left the reader unable to tell whether 90.9% was good.
const overdueTargetPct = computed(() => (OVERDUE_TARGET_RATE * 100).toFixed(0));

// A median cannot be summed out of daily buckets — it needs the underlying
// distribution — so the aggregator returns nothing rather than something wrong
// for a multi-day window. Saying that is better than printing a dash and
// leaving the reader to guess the page is broken.
const medianAcrossDaysUnavailable = computed(
  () =>
    metricAvailable('first_human_response') &&
    windowDays.value > 1 &&
    teamMetrics.first_human_response.p50 === null
);

const overdueShare = computed(() => {
  const eligible = Number(teamMetrics.ongoing_response.eligible_turns);
  const overdue = Number(teamMetrics.ongoing_response.overdue_turns);
  if (!Number.isFinite(eligible) || eligible <= 0) return null;
  return overdue / eligible;
});

const answerAccuracy = computed(
  () => teamMetrics.quality.answer_accuracy || {}
);
// Exact-template matching is the only check that runs today, so an account with
// nothing template-eligible would otherwise publish a red 0% that reads as
// "every answer was wrong".
const answerAccuracyScored = computed(
  () => Number(answerAccuracy.value.formal_eligible) > 0
);

const auditCoverage = computed(() => {
  const audited = Number(
    teamMetrics.quality.template_compliance?.eligible_finalized
  );
  const source = Number(factHealth.coverage?.source_conversations);
  if (!Number.isFinite(audited) || !Number.isFinite(source) || source <= 0)
    return null;
  return audited / source;
});

const selectedDetail = computed(() =>
  agentRows.value.find(
    r => Number(r.agent_user_id) === Number(selectedDetailId.value)
  )
);

const showDetailDrawer = computed({
  get: () => Boolean(selectedDetail.value),
  set: open => {
    if (!open) selectedDetailId.value = null;
  },
});

const closeDetailDrawer = () => {
  selectedDetailId.value = null;
};

const { panelRef: detailDrawerPanelRef } = useQaDrawer({
  open: showDetailDrawer,
  onClose: closeDetailDrawer,
});

// --- comparison chart -------------------------------------------------------
//
// One chart, three metrics, switched by the reader — not three charts stacked.
// Two full-height charts pushed the per-agent table, which is the part people
// act on, below the fold on every screen size.
//
// The switch changes the metric, never the shape: bars compare people, and a
// line drawn across four agents would imply a trend from one colleague to the
// next. Per-day trend lines belong on the topic pages, where the x-axis is time.

// Every metric is gated on its own denominator, since an agent can have plenty
// of replies and almost no ongoing turns.
const chartMetrics = computed(() => [
  {
    key: 'first_human_response',
    label: t('QA_DASHBOARD.AGENT_KPI.FRT_RATE'),
    available: metricAvailable('first_human_response'),
    rows: scoredRows.value,
    value: row => Math.round(Number(row.first_human_response.rate) * 1000) / 10,
    format: value => `${value}%`,
    color: '#0f766e',
    max: 100,
    target: targetRate.value * 100,
    targetLabel: t('QA_DASHBOARD.AGENT_KPI.CHART_TARGET', {
      value: `${(targetRate.value * 100).toFixed(0)}%`,
    }),
    lowerIsBetter: false,
  },
  {
    key: 'ongoing_response',
    // Not "by agent": the same metric is a bar per colleague on one axis and a
    // team rate per day on the other. The card title says which.
    label: t('QA_DASHBOARD.AGENT_KPI.ONGOING_OVERDUE'),
    available: metricAvailable('ongoing_response'),
    rows: sortedRows.value.filter(
      row => Number(row.ongoing_response?.eligible_turns) >= minSample.value
    ),
    value: row => {
      const stats = row.ongoing_response || {};
      const eligible = Number(stats.eligible_turns) || 0;
      const overdue = Number(stats.overdue_turns) || 0;
      return eligible > 0 ? Math.round((overdue / eligible) * 1000) / 10 : 0;
    },
    format: value => `${value}%`,
    color: '#eb6834',
    max: 100,
    target: OVERDUE_TARGET_RATE * 100,
    targetLabel: t('QA_DASHBOARD.AGENT_KPI.CHART_TARGET', {
      value: `${(OVERDUE_TARGET_RATE * 100).toFixed(0)}%`,
    }),
    lowerIsBetter: true,
  },
  {
    key: 'first_reply_median',
    label: t('QA_DASHBOARD.AGENT_KPI.CHART_METRIC_MEDIAN'),
    available: metricAvailable('first_human_response'),
    rows: scoredRows.value.filter(row => row.first_human_response?.p50 != null),
    value: row => Math.round(Number(row.first_human_response.p50) * 10) / 10,
    format: value => `${value}s`,
    color: '#3f6ea8',
    max: undefined,
    target: Number(definition.first_response_threshold_seconds) || 30,
    targetLabel: t('QA_DASHBOARD.AGENT_KPI.CHART_TARGET', {
      value: `${Number(definition.first_response_threshold_seconds) || 30}s`,
    }),
    lowerIsBetter: true,
  },
  {
    // Template compliance exists at team level only — the snapshot never breaks
    // it down per agent — so it can be a line over days but never a bar per
    // colleague. Offering it in both places would mean inventing the half we do
    // not have.
    key: 'template_compliance',
    label: t('QA_DASHBOARD.AGENT_KPI.TEMPLATE_COMPLIANCE'),
    teamOnly: true,
    available: true,
    rows: [],
    format: value => `${value}%`,
    color: '#10795c',
    max: 100,
    target: TEMPLATE_TARGET_RATE * 100,
    targetLabel: t('QA_DASHBOARD.AGENT_KPI.CHART_TARGET', {
      value: `${(TEMPLATE_TARGET_RATE * 100).toFixed(0)}%`,
    }),
    lowerIsBetter: false,
  },
]);

// What a metric needs to be offered depends on the axis. Bars need two agents
// with enough sample; a line needs two days carrying a number — and a team-only
// metric can satisfy the second without ever satisfying the first.
//
// Both lists are computed from the data alone, never from the current mode: the
// mode is chosen from them, so reading it here would be a cycle.
const agentChartMetrics = computed(() =>
  chartMetrics.value.filter(
    metric => !metric.teamOnly && metric.available && metric.rows.length >= 2
  )
);

const timeChartMetrics = computed(() =>
  chartMetrics.value.filter(
    metric =>
      metric.available &&
      pointsForMetric(metric.key).filter(value => value !== null).length >= 2
  )
);

const availableChartMetrics = computed(() =>
  effectiveChartMode.value === 'time'
    ? timeChartMetrics.value
    : agentChartMetrics.value
);

const activeChartMetric = computed(
  () =>
    availableChartMetrics.value.find(
      metric => metric.key === selectedChartMetric.value
    ) || availableChartMetrics.value[0]
);

// An account with two agents, one of them barely active, can never fill a bar
// chart — but its trend is the one thing worth looking at, and hanging the whole
// card on the bars hid it. The card appears for either.
const showCompareChart = computed(
  () => agentChartMetrics.value.length > 0 || showTimeMode.value
);

const availableChartModes = computed(() => [
  ...(agentChartMetrics.value.length > 0 ? ['agent'] : []),
  ...(showTimeMode.value ? ['time'] : []),
]);

const chartRows = computed(() => activeChartMetric.value?.rows || []);

// How many agents the reader cannot see, so a missing colleague is never a
// mystery.
const chartExcludedCount = computed(
  () => visibleRows.value.length - chartRows.value.length
);

const chartCollection = computed(() => {
  const metric = activeChartMetric.value;
  if (!metric) return { labels: [], datasets: [] };

  return {
    labels: metric.rows.map(row => agentName(row.agent_user_id)),
    datasets: [
      {
        label: metric.label,
        data: metric.rows.map(metric.value),
        backgroundColor: metric.color,
        borderRadius: 4,
        categoryPercentage: 0.7,
        barPercentage: 0.9,
      },
    ],
  };
});

// --- over time ---------------------------------------------------------------
//
// Bars answer "who", the line answers "when". Same metric, different axis: a
// line drawn across agents would invent a trend between colleagues, so the
// switch changes what the x-axis is rather than redrawing the same data.

const seriesDays = ref([]);
const seriesAvailable = ref(false);
const chartMode = ref('agent');

// Each metric knows how to read a day the same way it reads an agent.
const SERIES_VALUE = {
  first_human_response: day => {
    const rate = day.first_human_response?.rate;
    return rate === null || rate === undefined
      ? null
      : Math.round(Number(rate) * 1000) / 10;
  },
  ongoing_response: day => {
    const eligible = Number(day.ongoing_response?.eligible_turns) || 0;
    const overdue = Number(day.ongoing_response?.overdue_turns) || 0;
    return eligible > 0 ? Math.round((overdue / eligible) * 1000) / 10 : null;
  },
  first_reply_median: day => {
    const p50 = day.first_human_response?.p50;
    return p50 === null || p50 === undefined
      ? null
      : Math.round(Number(p50) * 10) / 10;
  },
  template_compliance: day => {
    const rate = day.template_compliance?.rate;
    return rate === null || rate === undefined
      ? null
      : Math.round(Number(rate) * 1000) / 10;
  },
};

// A day the window covered but nothing happened on is a gap, not a zero, so it
// is plotted as null and the line skips it.
const pointsForMetric = key => {
  const read = SERIES_VALUE[key];
  if (!read) return [];
  return seriesDays.value.map(read);
};

const seriesPoints = computed(() =>
  pointsForMetric(activeChartMetric.value?.key)
);

// Offered when any metric has a line worth drawing, not just the one showing.
const showTimeMode = computed(
  () =>
    seriesAvailable.value &&
    Object.keys(SERIES_VALUE).some(
      key => pointsForMetric(key).filter(value => value !== null).length >= 2
    )
);

// Never leaves the reader on an empty panel: honour the choice when it can be
// drawn, otherwise show whichever axis actually has something.
const effectiveChartMode = computed(() => {
  if (chartMode.value === 'time' && showTimeMode.value) return 'time';
  if (agentChartMetrics.value.length > 0) return 'agent';
  return showTimeMode.value ? 'time' : 'agent';
});

const seriesLabels = computed(() =>
  seriesDays.value.map(day => String(day.date || '').slice(5))
);

const timeCollection = computed(() => {
  const metric = activeChartMetric.value;
  if (!metric) return { labels: [], datasets: [] };

  return {
    labels: seriesLabels.value,
    datasets: [
      {
        label: metric.label,
        data: seriesPoints.value,
        borderColor: metric.color,
        backgroundColor: `${metric.color}22`,
        pointBackgroundColor: metric.color,
        pointRadius: 3,
        borderWidth: 2,
        tension: 0.25,
        fill: true,
        spanGaps: false,
      },
    ],
  };
});

const timeChartOptions = computed(() => {
  const metric = activeChartMetric.value;
  if (!metric) return {};

  return {
    layout: { padding: { top: 22 } },
    plugins: {
      legend: { display: false },
      qaTargetLine: { value: metric.target, label: metric.targetLabel },
      qaValueLabels: { enabled: true, format: metric.format, lastOnly: true },
    },
    scales: {
      x: {
        // A 30-day window would otherwise print 30 labels shoulder to shoulder.
        // Every point is still plotted; only the labels thin out.
        ticks: { autoSkip: true, maxTicksLimit: 10, maxRotation: 0 },
      },
      y: {
        beginAtZero: true,
        max: metric.max,
        ticks: {
          stepSize: metric.max ? 25 : undefined,
          callback: metric.format,
        },
      },
    },
  };
});

const chartOptions = computed(() => {
  const metric = activeChartMetric.value;
  if (!metric) return {};

  return {
    layout: { padding: { top: 22 } },
    plugins: {
      legend: { display: false },
      qaTargetLine: { value: metric.target, label: metric.targetLabel },
      qaValueLabels: { enabled: true, format: metric.format },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: metric.max,
        ticks: {
          stepSize: metric.max ? 25 : undefined,
          callback: metric.format,
        },
      },
    },
  };
});

// True getting-started state: no agents, response metrics all withheld, and no
// finalized template audits — nothing to show, so surface an onboarding card
// instead of a grid of dashes.
const pageEmpty = computed(
  () =>
    !agentRows.value.length &&
    allWithheld.value &&
    !(Number(teamMetrics.quality?.template_compliance?.eligible_finalized) > 0)
);

// The window is the first thing to suspect when a page comes back empty, so the
// empty state offers the wider range rather than describing it.
const canWiden = computed(() => windowDays.value < PRESET_DAYS.month);

const ensureAgents = async () => {
  const tasks = [];
  if (!agents.value.length) tasks.push(store.dispatch('agents/get'));
  if (!teams.value.length) tasks.push(store.dispatch('teams/get'));
  if (!inboxes.value.length) tasks.push(store.dispatch('inboxes/get'));
  try {
    await Promise.all(tasks);
  } catch (error) {
    // non-fatal: table still works with ids
  }
};

const requestParams = (overrides = {}) => {
  const params = {
    window_start: filters.window_start,
    window_end: filters.window_end,
    ...overrides,
  };
  if (selectedAgentId.value) params.agent_user_id = selectedAgentId.value;
  if (teamFilter.value) params.team_id = teamFilter.value;
  if (inboxFilter.value) params.inbox_id = inboxFilter.value;
  if (channelFilter.value) params.channel = channelFilter.value;
  if (shiftFilter.value) params.shift_state = shiftFilter.value;
  return params;
};

// Second read for the window immediately before this one. Failures are silent:
// a missing comparison drops the delta chips, it never blocks the page.
const loadComparison = async () => {
  previousTeam.value = null;
  try {
    const span = windowDays.value;
    const end = new Date(`${filters.window_start}T00:00:00`).getTime() - DAY_MS;
    const start = end - (span - 1) * DAY_MS;
    const response = await QaDashboardApi.getAgentKpis(
      requestParams({
        window_start: toDateInput(start),
        window_end: toDateInput(end),
      })
    );
    previousTeam.value = response?.data?.payload || null;
  } catch (error) {
    previousTeam.value = null;
  }
};

const loadKpis = async (opts = {}) => {
  isLoading.value = true;
  try {
    // Send the whole-day dates as-is; the server snaps them to day boundaries in
    // the account timezone and reads the pre-computed daily buckets.
    const params = requestParams();
    if (opts?.refresh === true) params.refresh = true;
    const response = await QaDashboardApi.getAgentKpis(params);
    const data = response?.data || {};
    const payload = data.payload || {};
    snapshotHash.value = data.snapshot_hash || payload.snapshot_hash || '';
    banner.value =
      data.unofficial_banner ||
      payload.unofficial_banner ||
      t('QA_DASHBOARD.AGENT_KPI.CALIBRATION_BANNER');
    calibrationOnly.value = data.calibration_only !== false;
    dataSource.value = data.data_source || 'live';
    lastUpdated.value = payload.aggregated?.computed_at || null;
    windowCapped.value = data.window_capped === true;
    if (data.today) reportingToday.value = String(data.today);
    Object.assign(definition, payload.definition || {});
    Object.assign(
      factHealth,
      payload.fact_health || {
        status: 'unknown',
        rates_available: false,
        metrics: {},
        freshness: {},
        coverage: {},
        missing: {},
      }
    );
    Object.assign(
      teamMetrics.first_human_response,
      payload.first_human_response || {}
    );
    Object.assign(teamMetrics.ongoing_response, payload.ongoing_response || {});
    Object.assign(teamMetrics.unassigned_queue, payload.unassigned_queue || {});
    Object.assign(teamMetrics.quality, payload.quality || {});
    Object.assign(teamMetrics.attribution, payload.attribution || {});
    agentRows.value = Array.isArray(payload.agents) ? payload.agents : [];
    loadComparison();
    loadSeries(opts);
  } catch (error) {
    showAlert(
      error.response?.data?.error || t('QA_DASHBOARD.AGENT_KPI.ERROR_LOAD')
    );
  } finally {
    isLoading.value = false;
  }
};

// The daily points come from the same pre-computed buckets the page already
// reads, so this is a cheap second call. Silent on failure: no trend means the
// switch stays hidden, never that the page breaks.
const loadSeries = async (opts = {}) => {
  try {
    const params = requestParams();
    if (opts?.refresh === true) params.refresh = true;
    const response = await QaDashboardApi.getAgentKpiSeries(params);
    const data = response?.data || {};
    seriesDays.value = Array.isArray(data.days) ? data.days : [];
    seriesAvailable.value = data.available === true;
  } catch (error) {
    seriesDays.value = [];
    seriesAvailable.value = false;
  }
};

// Refresh drops the cached recent-day buckets so the page reflects the latest facts.
const refreshKpis = () => loadKpis({ refresh: true });

const applyPreset = key => {
  filters.window_start = startForDaysFrom(PRESET_DAYS[key]);
  filters.window_end = todayInput.value;
  loadKpis();
};

// The range control owns the dates; picking one reloads immediately, because a
// range that needs a second Apply click is a range people forget to apply.
const applyWindow = ({ start, end }) => {
  if (!start || !end) return;
  filters.window_start = start;
  filters.window_end = end;
  loadKpis();
};

const csvValue = value => {
  const text = value === null || value === undefined ? '' : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const exportCsv = () => {
  // Follows the visible columns rather than a fixed list. The old header was
  // fixed and had drifted: first_reply_median_seconds exported a percentile,
  // which is null on every multi-day window, so the file carried an empty
  // column nobody had noticed.
  const dataColumns = tableColumns.value.filter(
    col => col.kind === 'count' || col.kind === 'seconds' || col.kind === 'text'
  );
  const header = [
    'agent',
    'email',
    ...dataColumns.map(col => columnLabel(col)),
    'scored',
  ];
  const lines = sortedRows.value.map(row =>
    [
      agentName(row.agent_user_id),
      agentEmail(row.agent_user_id),
      ...dataColumns.map(col => {
        const value = cellValue(col, row);
        return value === null ? '' : value;
      }),
      rowScored(row) ? 'yes' : 'no',
    ].map(csvValue)
  );
  const csv = [header.join(','), ...lines.map(line => line.join(','))].join(
    '\n'
  );
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `agent-kpi-${filters.window_start}_${filters.window_end}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// A card that says "8 misses" has to open exactly those 8, so the window and the
// filters the reader is looking at travel with the link. tab defaults to 'all'
// because the card counts everything in the window, not just what is still open.
const drillIncidents = (sourceType, extra = {}) => {
  const query = {
    source_type: sourceType,
    tab: 'all',
    from: filters.window_start,
    to: filters.window_end,
    ...extra,
  };
  if (inboxFilter.value) query.inbox_id = inboxFilter.value;
  if (teamFilter.value) query.team_id = teamFilter.value;
  if (selectedAgentId.value) query.agent_user_id = selectedAgentId.value;
  return accountScopedRoute('qa_monitor_incidents', {}, query);
};

// How many rows the link opens, shown on the card so the promise is visible
// before the click and checkable after it.
const lateReplyCount = computed(() => {
  const stats = teamMetrics.first_human_response;
  const eligible = Number(stats.eligible_count);
  const within = Number(stats.within_sla_count);
  if (!Number.isFinite(eligible) || !Number.isFinite(within)) return null;
  return Math.max(eligible - within, 0);
});

// Nothing template-eligible in the window is not a score of zero. A green card
// reading "0 / 0" claims a result nobody produced, and its drill link would open
// an empty queue.
const templateScored = computed(
  () => Number(teamMetrics.quality.template_compliance?.eligible_finalized) > 0
);

const templateMissCount = computed(() => {
  if (!templateScored.value) return null;

  const stats = teamMetrics.quality.template_compliance || {};
  const eligible = Number(stats.eligible_finalized);
  const correct = Number(stats.correct);
  if (!Number.isFinite(eligible) || !Number.isFinite(correct)) return null;
  return Math.max(eligible - correct, 0);
});

// "Reviewed 17 of 89" under the rate. A QA verdict now moves this number, so the
// reader has to be able to see how much of it is a human's judgement and how
// much is still the detector's — an unreviewed row keeps counting as a miss.
const templateReviewCoverage = computed(() => {
  const stats = teamMetrics.quality.template_compliance || {};
  if (!stats.review_adjusted) return '';

  const eligible = Number(stats.eligible_finalized);
  if (!Number.isFinite(eligible) || eligible <= 0) return '';

  const reviewed =
    Number(stats.review_passed || 0) + Number(stats.review_confirmed || 0);
  return t('QA_DASHBOARD.AGENT_KPI.TEMPLATE_REVIEWED', {
    reviewed: num(reviewed),
    total: num(eligible),
  });
});

const drillTemplateAudit = () =>
  accountScopedRoute('qa_dashboard_template_audit');
const drillSafetyAudit = () => accountScopedRoute('qa_dashboard_safety_audit');

// Template compliance has a page of its own now — the same figure, how it was
// built, and the cases behind it. The other cards still open the queue directly
// until their pages exist: a link is worth making only when it lands somewhere
// that answers the question the card raised.
const drillSafetyDetail = () =>
  accountScopedRoute(
    'qa_dashboard_agent_kpi_confirmed_safety',
    {},
    {
      from: filters.window_start,
      to: filters.window_end,
      ...(teamFilter.value ? { team_id: teamFilter.value } : {}),
      ...(inboxFilter.value ? { inbox_id: inboxFilter.value } : {}),
      ...(selectedAgentId.value
        ? { agent_user_id: selectedAgentId.value }
        : {}),
    }
  );

const drillFirstReplyDetail = () =>
  accountScopedRoute(
    'qa_dashboard_agent_kpi_first_reply',
    {},
    {
      from: filters.window_start,
      to: filters.window_end,
      ...(teamFilter.value ? { team_id: teamFilter.value } : {}),
      ...(inboxFilter.value ? { inbox_id: inboxFilter.value } : {}),
      ...(selectedAgentId.value
        ? { agent_user_id: selectedAgentId.value }
        : {}),
    }
  );

const drillTemplateDetail = () =>
  accountScopedRoute(
    'qa_dashboard_agent_kpi_template_compliance',
    {},
    {
      from: filters.window_start,
      to: filters.window_end,
      ...(teamFilter.value ? { team_id: teamFilter.value } : {}),
      ...(inboxFilter.value ? { inbox_id: inboxFilter.value } : {}),
      ...(selectedAgentId.value
        ? { agent_user_id: selectedAgentId.value }
        : {}),
    }
  );

const openDetail = row => {
  selectedDetailId.value = row.agent_user_id;
};

watch(
  [selectedAgentId, teamFilter, inboxFilter, channelFilter, shiftFilter],
  () => {
    loadKpis();
  }
);

// Coming back from a topic page carries the window it was showing, so the round
// trip does not silently reset the reader to the default seven days.
const readWindowFromQuery = () => {
  const query = route?.query || {};
  if (query.from) filters.window_start = String(query.from);
  if (query.to) filters.window_end = String(query.to);
  if (query.team_id) teamFilter.value = String(query.team_id);
  if (query.inbox_id) inboxFilter.value = String(query.inbox_id);
  if (query.agent_user_id) selectedAgentId.value = String(query.agent_user_id);
};

// The account record already carries a timezone, so the very first request can
// ask for the right day instead of the reader's. The response then confirms it
// with the QA reporting timezone, which may differ from the account's.
const bootstrapReportingDay = () => {
  const accountId = route?.params?.accountId;
  const account = store.getters['accounts/getAccount']?.(accountId) || {};
  const today = todayInZone(account.timezone);
  if (!today || today === toDateInput(Date.now())) return;

  const untouched = filters.window_end === toDateInput(Date.now());
  reportingToday.value = today;
  if (untouched) {
    filters.window_end = today;
    filters.window_start = startForDaysFrom(PRESET_DAYS.week);
  }
};

// The trigger and the menu both stop propagation, so anything reaching the
// document is genuinely outside. Guarding on element containment instead would
// fail here for the reason it failed on the date picker: Vue re-renders the
// menu before this listener runs, and the clicked node is no longer in it.
const closeColumnsMenu = () => {
  columnsMenuOpen.value = false;
};

onMounted(async () => {
  document.addEventListener('click', closeColumnsMenu);
  const stored = readStoredColumns();
  if (stored) selectedColumnKeys.value = stored;
  bootstrapReportingDay();
  readWindowFromQuery();
  await ensureAgents();
  await loadKpis();
});

onBeforeUnmount(() => {
  document.removeEventListener('click', closeColumnsMenu);
});
</script>

<template>
  <div
    class="qa-page"
    data-qa-marker="agent-kpi-deepen-20260731.18"
    :data-qa-compile-marker="compileMarker"
  >
    <div class="qa-shell">
      <div class="qa-header">
        <div class="qa-header-left">
          <h1 class="qa-title">{{ t('QA_DASHBOARD.AGENT_KPI.TITLE') }}</h1>
          <p class="qa-desc">{{ t('QA_DASHBOARD.AGENT_KPI.DESCRIPTION') }}</p>
          <span
            v-if="calibrationOnly"
            class="qa-pill qa-pill--warn"
            role="status"
            :title="banner"
          >
            {{ t('QA_DASHBOARD.AGENT_KPI.CALIBRATION_BADGE') }}
          </span>
        </div>
        <div class="qa-header-actions">
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading || !sortedRows.length"
            @click="exportCsv"
          >
            {{ t('QA_DASHBOARD.AGENT_KPI.EXPORT_CSV') }}
          </button>
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="refreshKpis"
          >
            {{
              isLoading
                ? t('QA_DASHBOARD.AGENT_KPI.LOADING')
                : t('QA_DASHBOARD.AGENT_KPI.REFRESH')
            }}
          </button>
        </div>
      </div>

      <div class="qa-toolbar">
        <div class="qa-toolbar-left">
          <QaDateRangeSelect
            :start="filters.window_start"
            :end="filters.window_end"
            :min-date="minDateInput"
            :max-date="todayInput"
            :today="reportingToday"
            @change="applyWindow"
          />
          <label class="qa-field">
            <span class="qa-label">{{ t('QA_DASHBOARD.AGENT_KPI.TEAM') }}</span>
            <select v-model="teamFilter" class="qa-select">
              <option value="">
                {{ t('QA_DASHBOARD.AGENT_KPI.ALL_TEAMS') }}
              </option>
              <option
                v-for="team in teams"
                :key="team.id"
                :value="String(team.id)"
              >
                {{ team.name }}
              </option>
            </select>
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.AGENT_KPI.INBOX')
            }}</span>
            <select v-model="inboxFilter" class="qa-select">
              <option value="">
                {{ t('QA_DASHBOARD.AGENT_KPI.ALL_INBOXES') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="String(inbox.id)"
              >
                {{ inbox.name }}
              </option>
            </select>
          </label>
          <label class="qa-field">
            <span class="qa-label">{{
              t('QA_DASHBOARD.AGENT_KPI.CHANNEL')
            }}</span>
            <select v-model="channelFilter" class="qa-select">
              <option value="">
                {{ t('QA_DASHBOARD.AGENT_KPI.ALL_CHANNELS') }}
              </option>
              <option v-for="ch in channelOptions" :key="ch" :value="ch">
                {{ ch }}
              </option>
            </select>
          </label>
          <label class="qa-field">
            <span class="qa-label">{{ t('QA_DASHBOARD.FORM.AGENT') }}</span>
            <select v-model="selectedAgentId" class="qa-select">
              <option value="">
                {{ t('QA_DASHBOARD.AGENT_KPI.ALL_AGENTS') }}
              </option>
              <option
                v-for="agent in agents"
                :key="agent.id"
                :value="String(agent.id)"
              >
                {{ agent.name || agent.available_name || agent.email }}
              </option>
            </select>
          </label>
          <button
            type="button"
            class="qa-btn"
            :disabled="isLoading"
            @click="loadKpis"
          >
            {{ t('QA_DASHBOARD.AGENT_KPI.APPLY') }}
          </button>
        </div>

        <details class="qa-disclosure" style="width: 100%; margin-top: 4px">
          <summary>{{ t('QA_DASHBOARD.AGENT_KPI.FILTERS_MORE') }}</summary>
          <div class="qa-toolbar-left" style="padding-top: 8px">
            <label class="qa-field">
              <span class="qa-label">{{
                t('QA_DASHBOARD.AGENT_KPI.LANGUAGE')
              }}</span>
              <select v-model="languageFilter" class="qa-select">
                <option value="">
                  {{ t('QA_DASHBOARD.AGENT_KPI.ALL_LANGUAGES') }}
                </option>
                <option
                  v-for="lang in languageOptions"
                  :key="lang"
                  :value="lang"
                >
                  {{ lang }}
                </option>
              </select>
            </label>
            <label class="qa-field">
              <span class="qa-label">{{
                t('QA_DASHBOARD.AGENT_KPI.SHIFT')
              }}</span>
              <select v-model="shiftFilter" class="qa-select">
                <option value="">
                  {{ t('QA_DASHBOARD.AGENT_KPI.SHIFT_ALL') }}
                </option>
                <option value="on_shift">
                  {{ t('QA_DASHBOARD.AGENT_KPI.SHIFT_ON') }}
                </option>
                <option value="off_shift">
                  {{ t('QA_DASHBOARD.AGENT_KPI.SHIFT_OFF') }}
                </option>
              </select>
            </label>
          </div>
          <p class="qa-hint" style="margin: 8px 0 0">
            {{ t('QA_DASHBOARD.AGENT_KPI.FILTER_NOTE') }}
          </p>
        </details>
      </div>

      <div class="qa-content">
        <section class="qa-meta-strip" :aria-busy="isLoading">
          <span :class="factHealthPillClass" role="status">
            {{ t('QA_DASHBOARD.AGENT_KPI.FACT_HEALTH') }} ·
            {{ factStatusLabel }}
          </span>
          <span
            class="qa-pill qa-pill--muted"
            role="status"
            :title="t('QA_DASHBOARD.AGENT_KPI.SOURCE_HINT')"
          >
            {{
              dataSource === 'pre_computed'
                ? t('QA_DASHBOARD.AGENT_KPI.SOURCE_PRECOMPUTED')
                : t('QA_DASHBOARD.AGENT_KPI.SOURCE_LIVE')
            }}
          </span>
          <span v-if="lastUpdatedText" class="qa-hint">{{
            lastUpdatedText
          }}</span>
          <span v-if="windowCapped" class="qa-pill qa-pill--warn" role="status">
            {{
              t('QA_DASHBOARD.AGENT_KPI.WINDOW_CAPPED', {
                days: MAX_WINDOW_DAYS,
              })
            }}
          </span>
          <span v-if="!factComplete">
            {{
              t('QA_DASHBOARD.AGENT_KPI.FACT_COVERAGE', {
                projected: num(factHealth.coverage?.projected_conversations),
                source: num(factHealth.coverage?.source_conversations),
              })
            }}
          </span>
          <span v-if="allWithheld" class="qa-hint">
            {{ t('QA_DASHBOARD.AGENT_KPI.FACT_RATES_WITHHELD') }}
          </span>
        </section>

        <section v-if="pageEmpty" class="qa-card">
          <div class="qa-card-body">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.AGENT_KPI.ONBOARDING_TITLE') }}
            </h2>
            <p class="qa-hint qa-hint--block">
              {{ t('QA_DASHBOARD.AGENT_KPI.ONBOARDING_BODY') }}
            </p>
            <p class="qa-hint qa-hint--block">
              {{
                t('QA_DASHBOARD.AGENT_KPI.EMPTY_WINDOW_HINT', {
                  days: windowDays,
                })
              }}
            </p>
            <button
              v-if="canWiden"
              type="button"
              class="qa-btn qa-btn--sm"
              @click="applyPreset('month')"
            >
              {{
                t('QA_DASHBOARD.AGENT_KPI.EMPTY_WINDOW_ACTION', {
                  days: PRESET_DAYS.month,
                })
              }}
            </button>
          </div>
        </section>

        <section v-if="!pageEmpty" class="qa-kpi-group">
          <h2 class="qa-group-title">
            {{ t('QA_DASHBOARD.AGENT_KPI.RESPONSE_GROUP') }}
            <span v-if="anyPartial" class="qa-pill qa-pill--warn">
              {{ t('QA_DASHBOARD.AGENT_KPI.FACT_PARTIAL_BADGE') }}
            </span>
            <span v-if="comparisonNote" class="qa-group-note">
              {{ comparisonNote }}
            </span>
          </h2>
          <p v-if="anyPartial" class="qa-hint qa-hint--block">
            {{
              t('QA_DASHBOARD.AGENT_KPI.FACT_PARTIAL_NOTE', {
                projected: num(factHealth.coverage?.projected_conversations),
                source: num(factHealth.coverage?.source_conversations),
              })
            }}
          </p>
          <div class="qa-kpis">
            <router-link
              v-if="metricAvailable('first_human_response')"
              class="qa-kpi qa-kpi--tone-accent qa-kpi--clickable"
              data-qa-metric="first_human_response"
              :to="drillFirstReplyDetail()"
            >
              <div class="qa-kpi-top">
                <span class="qa-kpi-label">{{
                  t('QA_DASHBOARD.AGENT_KPI.FRT_RATE')
                }}</span>
                <span v-if="frtDelta" :class="deltaClass(frtDelta)">{{
                  deltaText(frtDelta)
                }}</span>
              </div>
              <div class="qa-kpi-value">
                {{ frtPct(teamMetrics.first_human_response.rate) }}
              </div>
              <span
                v-if="teamMetrics.first_human_response.rate != null"
                class="qa-progress"
                :class="rateTone(teamMetrics.first_human_response.rate)"
              >
                <span
                  class="qa-progress-fill"
                  :style="{
                    width: `${rateBarWidth(teamMetrics.first_human_response.rate)}%`,
                  }"
                />
              </span>
              <div class="qa-kpi-foot">
                <span class="qa-basis">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.BASIS_REPLIES', {
                      within: frtNum(
                        teamMetrics.first_human_response.within_sla_count
                      ),
                      total: frtNum(
                        teamMetrics.first_human_response.eligible_count
                      ),
                    })
                  }}
                </span>
                <span class="qa-target">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.TARGET_RATE', {
                      pct: (targetRate * 100).toFixed(0),
                      seconds:
                        definition.first_response_threshold_seconds || 30,
                    })
                  }}
                </span>
              </div>
              <div class="qa-kpi-sub">
                <template v-if="medianAcrossDaysUnavailable">
                  {{ t('QA_DASHBOARD.AGENT_KPI.MEDIAN_PER_DAY_ONLY') }}
                </template>
                {{
                  medianAcrossDaysUnavailable
                    ? ''
                    : t('QA_DASHBOARD.AGENT_KPI.KPI_P50', {
                        value: frtSeconds(teamMetrics.first_human_response.p50),
                      })
                }}
                <template v-if="teamMetrics.first_human_response.p95 != null">
                  ·
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.KPI_P95', {
                      value: frtSeconds(teamMetrics.first_human_response.p95),
                    })
                  }}
                </template>
              </div>
              <span v-if="lateReplyCount !== null" class="qa-kpi-go">
                {{
                  t('QA_DASHBOARD.AGENT_KPI.OPEN_LATE', {
                    count: lateReplyCount,
                  })
                }}
              </span>
            </router-link>
            <div
              v-else
              class="qa-kpi qa-kpi--muted qa-kpi--tone-neutral"
              data-qa-metric="first_human_response"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.FRT_RATE') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--sm">—</div>
              <div class="qa-kpi-sub">
                {{ metricReasonText('first_human_response') }}
              </div>
            </div>

            <router-link
              v-if="metricAvailable('ongoing_response')"
              class="qa-kpi qa-kpi--tone-warn qa-kpi--clickable"
              data-qa-metric="ongoing_response"
              :to="drillIncidents('slow_reply')"
            >
              <div class="qa-kpi-top">
                <span class="qa-kpi-label">{{
                  t('QA_DASHBOARD.AGENT_KPI.ONGOING_OVERDUE')
                }}</span>
                <span v-if="overdueDelta" :class="deltaClass(overdueDelta)">
                  {{ deltaText(overdueDelta) }}
                </span>
              </div>
              <div class="qa-kpi-value qa-kpi-value--warn">
                {{ ongoingNum(teamMetrics.ongoing_response.overdue_turns) }}
              </div>
              <div class="qa-kpi-foot">
                <span class="qa-basis">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.BASIS_TURNS', {
                      share: overdueShare === null ? '—' : pct(overdueShare),
                      total: ongoingNum(
                        teamMetrics.ongoing_response.eligible_turns
                      ),
                    })
                  }}
                </span>
                <span class="qa-target">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.TARGET_SHARE', {
                      pct: overdueTargetPct,
                      seconds: definition.ongoing_threshold_seconds || 180,
                    })
                  }}
                </span>
              </div>
              <div class="qa-kpi-sub">
                {{
                  t('QA_DASHBOARD.AGENT_KPI.KPI_UNANSWERED', {
                    count: ongoingNum(
                      teamMetrics.ongoing_response.final_unanswered
                    ),
                  })
                }}
              </div>
              <span class="qa-kpi-go">
                {{
                  t('QA_DASHBOARD.AGENT_KPI.OPEN_TURNS', {
                    count: ongoingNum(
                      teamMetrics.ongoing_response.overdue_turns
                    ),
                  })
                }}
              </span>
            </router-link>
            <div
              v-else
              class="qa-kpi qa-kpi--muted qa-kpi--tone-neutral"
              data-qa-metric="ongoing_response"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.ONGOING_OVERDUE') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--sm">—</div>
              <div class="qa-kpi-sub">
                {{ metricReasonText('ongoing_response') }}
              </div>
            </div>

            <div
              v-if="metricAvailable('unassigned_queue')"
              class="qa-kpi qa-kpi--tone-bad"
              data-qa-metric="unassigned_queue"
            >
              <div class="qa-kpi-top">
                <span class="qa-kpi-label">{{
                  t('QA_DASHBOARD.AGENT_KPI.UNASSIGNED_BREACH')
                }}</span>
                <span v-if="queueDelta" :class="deltaClass(queueDelta)">
                  {{ deltaText(queueDelta) }}
                </span>
              </div>
              <div class="qa-kpi-value qa-kpi-value--bad">
                {{ queueNum(teamMetrics.unassigned_queue.breach_count) }}
              </div>
              <div class="qa-kpi-foot">
                <span class="qa-basis">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.KPI_COUNT', {
                      count: queueNum(teamMetrics.unassigned_queue.count),
                    })
                  }}
                </span>
                <span class="qa-target">{{
                  t('QA_DASHBOARD.AGENT_KPI.TARGET_ZERO')
                }}</span>
              </div>
              <div class="qa-kpi-sub">
                {{ t('QA_DASHBOARD.AGENT_KPI.QUEUE_NOT_PERSONAL') }}
              </div>
            </div>
            <div
              v-else
              class="qa-kpi qa-kpi--muted qa-kpi--tone-neutral"
              data-qa-metric="unassigned_queue"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.UNASSIGNED_BREACH') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--sm">—</div>
              <div class="qa-kpi-sub">
                {{ metricReasonText('unassigned_queue') }}
              </div>
            </div>
          </div>
        </section>

        <section v-if="!pageEmpty" class="qa-kpi-group">
          <h2 class="qa-group-title">
            {{ t('QA_DASHBOARD.AGENT_KPI.QUALITY_GROUP') }}
          </h2>
          <div class="qa-kpis">
            <router-link
              v-if="!templateScored"
              class="qa-kpi qa-kpi--tone-neutral qa-kpi--clickable"
              data-qa-metric="template_compliance"
              :to="drillTemplateDetail()"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.TEMPLATE_COMPLIANCE') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--sm">
                {{ t('QA_DASHBOARD.AGENT_KPI.NOT_SCORED') }}
              </div>
              <div class="qa-kpi-sub">
                {{ t('QA_DASHBOARD.AGENT_KPI.TEMPLATE_NOT_SCORED_BODY') }}
              </div>
              <span class="qa-kpi-go">
                {{ t('QA_DASHBOARD.AGENT_KPI.OPEN_TOPIC') }}
              </span>
            </router-link>
            <router-link
              v-else
              class="qa-kpi qa-kpi--tone-good qa-kpi--clickable"
              data-qa-metric="template_compliance"
              :to="drillTemplateDetail()"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.TEMPLATE_COMPLIANCE') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--good">
                {{ pct(teamMetrics.quality.template_compliance?.rate) }}
              </div>
              <div class="qa-kpi-foot">
                <span class="qa-basis">
                  {{ num(teamMetrics.quality.template_compliance?.correct) }} /
                  {{
                    num(
                      teamMetrics.quality.template_compliance
                        ?.eligible_finalized
                    )
                  }}
                </span>
                <span class="qa-target">{{
                  t('QA_DASHBOARD.AGENT_KPI.AUDITED')
                }}</span>
              </div>
              <!-- This rate is adjusted by QA's verdicts, so it must say how much
                   of it a human has actually ruled on. Without this the number
                   moves when someone presses Pass and nothing on screen explains
                   why. -->
              <div v-if="templateReviewCoverage" class="qa-kpi-note">
                {{ templateReviewCoverage }}
              </div>
              <span v-if="templateMissCount !== null" class="qa-kpi-go">
                {{
                  t('QA_DASHBOARD.AGENT_KPI.OPEN_MISSES', {
                    count: templateMissCount,
                  })
                }}
              </span>
            </router-link>
            <router-link
              class="qa-kpi qa-kpi--tone-bad qa-kpi--clickable"
              data-qa-metric="confirmed_safety"
              :to="drillSafetyDetail()"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.SAFETY_CONFIRMED') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--bad">
                {{ num(teamMetrics.quality.safety_confirmed_violations) }}
              </div>
              <div class="qa-kpi-foot">
                <span class="qa-basis">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.KPI_PENDING', {
                      count: num(
                        teamMetrics.quality.safety_pending_machine_flags
                      ),
                    })
                  }}
                </span>
                <span class="qa-target">{{
                  t('QA_DASHBOARD.AGENT_KPI.TARGET_ZERO')
                }}</span>
              </div>
            </router-link>
            <div class="qa-kpi qa-kpi--tone-neutral">
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.ANSWER_ACCURACY') }}
              </div>
              <template v-if="answerAccuracyScored">
                <div class="qa-kpi-value">{{ pct(answerAccuracy.rate) }}</div>
                <div class="qa-kpi-foot">
                  <span class="qa-basis">
                    {{ num(answerAccuracy.formal_correct) }} /
                    {{ num(answerAccuracy.formal_eligible) }}
                  </span>
                  <span class="qa-target">{{
                    t('QA_DASHBOARD.AGENT_KPI.EXACT_MATCH_ONLY')
                  }}</span>
                </div>
              </template>
              <template v-else>
                <div class="qa-kpi-value qa-kpi-value--sm">
                  {{ t('QA_DASHBOARD.AGENT_KPI.NOT_SCORED') }}
                </div>
                <div class="qa-kpi-sub">
                  {{ t('QA_DASHBOARD.AGENT_KPI.NOT_SCORED_BODY') }}
                </div>
              </template>
            </div>
            <div
              v-if="auditCoverage !== null"
              class="qa-kpi qa-kpi--tone-neutral"
            >
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.AUDIT_COVERAGE') }}
              </div>
              <div class="qa-kpi-value">{{ pct(auditCoverage) }}</div>
              <div class="qa-kpi-foot">
                <span class="qa-basis">
                  {{
                    t('QA_DASHBOARD.AGENT_KPI.AUDIT_COVERAGE_BASIS', {
                      audited: num(
                        teamMetrics.quality.template_compliance
                          ?.eligible_finalized
                      ),
                      total: num(factHealth.coverage?.source_conversations),
                    })
                  }}
                </span>
                <span class="qa-target">{{
                  t('QA_DASHBOARD.AGENT_KPI.SAMPLE_NOT_CENSUS')
                }}</span>
              </div>
            </div>
          </div>
        </section>

        <section class="qa-card">
          <div class="qa-card-header qa-table-header">
            <h2 class="qa-card-title">
              {{ t('QA_DASHBOARD.AGENT_KPI.PER_AGENT') }}
            </h2>
            <span class="qa-hint">
              {{
                t('QA_DASHBOARD.AGENT_KPI.TABLE_SUMMARY', {
                  count: sortedRows.length,
                })
              }}
            </span>
            <span v-if="teamMedianRate !== null" class="qa-pill qa-pill--muted">
              {{
                t('QA_DASHBOARD.AGENT_KPI.TEAM_MEDIAN', {
                  value: pct(teamMedianRate),
                })
              }}
            </span>
            <div class="qa-colpick">
              <button
                type="button"
                class="qa-btn qa-btn--sm"
                :aria-expanded="columnsMenuOpen ? 'true' : 'false'"
                @click.stop="columnsMenuOpen = !columnsMenuOpen"
              >
                {{ t('QA_DASHBOARD.AGENT_KPI.COLUMNS_BUTTON') }}
                <span class="qa-colpick-count">
                  {{ tableColumns.length }}
                </span>
              </button>
              <div v-if="columnsMenuOpen" class="qa-colpick-menu" @click.stop>
                <div
                  v-for="group in columnMenuGroups"
                  :key="group.id"
                  class="qa-colpick-group"
                >
                  <div class="qa-colpick-group-head">
                    <span>{{ group.label }}</span>
                    <button
                      v-if="group.columns.length > 1"
                      type="button"
                      class="qa-linkbtn"
                      @click="toggleColumnGroup(group)"
                    >
                      {{ t('QA_DASHBOARD.AGENT_KPI.COLUMNS_GROUP_TOGGLE') }}
                    </button>
                  </div>
                  <label
                    v-for="col in group.locked"
                    :key="col.key"
                    class="qa-colpick-opt qa-colpick-opt--locked"
                  >
                    <input type="checkbox" checked disabled />
                    <span>{{ columnLabel(col) }}</span>
                    <small>{{
                      t('QA_DASHBOARD.AGENT_KPI.COLUMNS_LOCKED')
                    }}</small>
                  </label>
                  <label
                    v-for="col in group.columns"
                    :key="col.key"
                    class="qa-colpick-opt"
                  >
                    <input
                      type="checkbox"
                      :checked="isColumnOn(col.key)"
                      @change="toggleColumn(col.key)"
                    />
                    <span>{{ columnLabel(col) }}</span>
                  </label>
                </div>
                <div class="qa-colpick-foot">
                  <button
                    type="button"
                    class="qa-linkbtn"
                    @click="resetColumns"
                  >
                    {{ t('QA_DASHBOARD.AGENT_KPI.COLUMNS_RESET') }}
                  </button>
                  <button
                    type="button"
                    class="qa-linkbtn"
                    @click="showAllColumns"
                  >
                    {{ t('QA_DASHBOARD.AGENT_KPI.COLUMNS_ALL') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div class="qa-card-body" style="overflow-x: auto">
            <table class="qa-table qa-table--rich qa-table--agents">
              <thead>
                <tr>
                  <th
                    v-for="col in tableColumns"
                    :key="col.key"
                    :class="[
                      col.kind === 'count' || col.kind === 'seconds'
                        ? 'qa-num'
                        : '',
                      col.key === 'staff' ? 'qa-col-sticky' : '',
                    ]"
                  >
                    {{ columnLabel(col) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="!sortedRows.length">
                  <td :colspan="tableColumns.length" class="qa-empty">
                    {{ t('QA_DASHBOARD.AGENT_KPI.EMPTY') }}
                  </td>
                </tr>
                <tr
                  v-for="row in sortedRows"
                  :key="row.agent_user_id"
                  :class="`qa-row-sev qa-row-sev--${rowSeverity(row)}`"
                >
                  <td
                    v-for="col in tableColumns"
                    :key="col.key"
                    :class="[
                      col.kind === 'count' || col.kind === 'seconds'
                        ? 'qa-num'
                        : '',
                      col.key === 'staff' ? 'qa-col-sticky' : '',
                    ]"
                  >
                    <span v-if="col.kind === 'person'" class="qa-cell-person">
                      <Avatar
                        :src="agentThumbnail(row.agent_user_id)"
                        :name="agentName(row.agent_user_id)"
                        :size="28"
                        rounded-full
                        hide-offline-status
                      />
                      <span class="qa-cell-person-text">
                        <strong>{{ agentName(row.agent_user_id) }}</strong>
                        <small v-if="agentEmail(row.agent_user_id)">{{
                          agentEmail(row.agent_user_id)
                        }}</small>
                      </span>
                    </span>
                    <button
                      v-else-if="col.kind === 'actions'"
                      type="button"
                      class="qa-btn qa-btn--sm"
                      @click="openDetail(row)"
                    >
                      {{ t('QA_DASHBOARD.AGENT_KPI.DRILL') }}
                    </button>
                    <span
                      v-else-if="col.kind === 'text'"
                      class="qa-chip"
                      :class="{ 'qa-chip--empty': cellMain(col, row) === '—' }"
                      >{{ cellMain(col, row) }}</span
                    >
                    <span
                      v-else
                      class="qa-metric-cell"
                      :class="cellToneClass(col, row)"
                    >
                      <span class="qa-cell-count">{{
                        cellMain(col, row)
                      }}</span>
                      <span v-if="cellSub(col, row)" class="qa-cell-share">{{
                        cellSub(col, row)
                      }}</span>
                      <span
                        v-if="cellMovementText(col, row)"
                        :class="cellMovementClass(col, row)"
                        >{{ cellMovementText(col, row) }}</span
                      >
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div class="qa-card-footer">
            <span class="qa-legend">
              <i class="qa-legend-dot qa-legend-dot--bad" />
              {{ t('QA_DASHBOARD.AGENT_KPI.LEGEND_BAD') }}
            </span>
            <span class="qa-legend">
              <i class="qa-legend-dot qa-legend-dot--warn" />
              {{ t('QA_DASHBOARD.AGENT_KPI.LEGEND_WARN') }}
            </span>
            <span class="qa-legend">
              <i class="qa-legend-dot qa-legend-dot--good" />
              {{ t('QA_DASHBOARD.AGENT_KPI.LEGEND_GOOD') }}
            </span>
            <span class="qa-legend">
              <i class="qa-legend-dot qa-legend-dot--mute" />
              {{ t('QA_DASHBOARD.AGENT_KPI.LEGEND_MUTE', { min: minSample }) }}
            </span>
          </div>
        </section>

        <section
          v-if="showCompareChart"
          class="qa-card"
          data-qa-chart="compare"
          :data-qa-chart-metric="activeChartMetric.key"
        >
          <div class="qa-card-header qa-table-header">
            <h2 class="qa-card-title">
              {{
                effectiveChartMode === 'time'
                  ? t('QA_DASHBOARD.AGENT_KPI.CHART_TREND_TITLE')
                  : t('QA_DASHBOARD.AGENT_KPI.CHART_COMPARE_TITLE')
              }}
            </h2>
            <span class="qa-hint">{{
              effectiveChartMode === 'time'
                ? t('QA_DASHBOARD.AGENT_KPI.CHART_SCOPE_TIME', {
                    days: seriesDays.length,
                  })
                : t('QA_DASHBOARD.AGENT_KPI.CHART_SCOPE', { min: minSample })
            }}</span>
            <span class="qa-header-spacer" />
            <span
              v-if="availableChartModes.length > 1"
              class="qa-preset"
              role="group"
              :aria-label="t('QA_DASHBOARD.AGENT_KPI.CHART_MODE')"
            >
              <button
                v-for="mode in availableChartModes"
                :key="mode"
                type="button"
                class="qa-preset-btn"
                :class="{ 'qa-preset-btn--on': effectiveChartMode === mode }"
                :aria-pressed="effectiveChartMode === mode"
                :data-qa-chart-mode="mode"
                @click="chartMode = mode"
              >
                {{
                  t(`QA_DASHBOARD.AGENT_KPI.CHART_MODE_${mode.toUpperCase()}`)
                }}
              </button>
            </span>
            <span
              v-if="availableChartMetrics.length > 1"
              class="qa-preset"
              role="group"
              :aria-label="t('QA_DASHBOARD.AGENT_KPI.CHART_COMPARE_TITLE')"
            >
              <button
                v-for="metric in availableChartMetrics"
                :key="metric.key"
                type="button"
                class="qa-preset-btn"
                :class="{
                  'qa-preset-btn--on': metric.key === activeChartMetric.key,
                }"
                :aria-pressed="metric.key === activeChartMetric.key"
                @click="selectedChartMetric = metric.key"
              >
                {{ metric.label }}
              </button>
            </span>
          </div>
          <div class="qa-card-body qa-chart-body qa-chart-body--compact">
            <BarChart
              v-if="effectiveChartMode === 'agent'"
              :collection="chartCollection"
              :chart-options="chartOptions"
            />
            <LineChart
              v-else
              :collection="timeCollection"
              :chart-options="timeChartOptions"
            />
          </div>
          <div class="qa-card-footer">
            <span class="qa-legend">
              {{
                activeChartMetric.lowerIsBetter
                  ? t('QA_DASHBOARD.AGENT_KPI.CHART_LOWER_BETTER')
                  : t('QA_DASHBOARD.AGENT_KPI.CHART_HIGHER_BETTER')
              }}
            </span>
            <span
              v-if="effectiveChartMode === 'agent' && chartExcludedCount > 0"
              class="qa-legend"
            >
              {{
                t('QA_DASHBOARD.AGENT_KPI.CHART_EXCLUDED', {
                  count: chartExcludedCount,
                  min: minSample,
                })
              }}
            </span>
          </div>
        </section>

        <p v-if="definition.version" class="qa-hint">
          {{
            t('QA_DASHBOARD.AGENT_KPI.DEFINITION', {
              version: definition.version,
            })
          }}
        </p>

        <details class="qa-disclosure">
          <summary>{{ t('QA_DASHBOARD.AGENT_KPI.DATA_QUALITY') }}</summary>
          <p class="qa-desc">
            <span v-if="snapshotHash" class="qa-mono">{{
              snapshotHash.slice(0, 12)
            }}</span>
            <span v-if="snapshotHash"> · </span>
            {{
              t('QA_DASHBOARD.AGENT_KPI.FACT_WATERMARK', {
                watermark: factHealth.freshness?.watermark_at || '—',
              })
            }}
          </p>
        </details>
      </div>
    </div>

    <div
      v-if="selectedDetail"
      class="qa-drawer-backdrop"
      role="presentation"
      @click.self="closeDetailDrawer"
    >
      <aside
        ref="detailDrawerPanelRef"
        class="qa-drawer"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        :aria-label="
          t('QA_DASHBOARD.AGENT_KPI.DETAIL_TITLE', {
            name: agentName(selectedDetail.agent_user_id),
          })
        "
      >
        <div class="qa-drawer-header">
          <div class="qa-cell-person">
            <Avatar
              :src="agentThumbnail(selectedDetail.agent_user_id)"
              :name="agentName(selectedDetail.agent_user_id)"
              :size="36"
              rounded-full
              hide-offline-status
            />
            <div>
              <h2 class="qa-card-title">
                {{ agentName(selectedDetail.agent_user_id) }}
              </h2>
              <span v-if="selectedDetail.language_hint" class="qa-chip">
                {{ selectedDetail.language_hint }}
              </span>
            </div>
          </div>
          <button
            type="button"
            class="qa-btn qa-btn--sm"
            @click="closeDetailDrawer"
          >
            {{ t('QA_DASHBOARD.AGENT_KPI.CLOSE_DETAIL') }}
          </button>
        </div>
        <div class="qa-drawer-body">
          <div class="qa-kpis">
            <div class="qa-kpi qa-kpi--tone-neutral">
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.WORKLOAD') }}
              </div>
              <div class="qa-kpi-value">
                {{ frtNum(selectedDetail.workload?.frt_episodes) }}
              </div>
              <div class="qa-kpi-sub">
                claims {{ claimNum(selectedDetail.workload?.claim_episodes) }} ·
                ongoing {{ ongoingNum(selectedDetail.workload?.ongoing_turns) }}
              </div>
            </div>
            <div class="qa-kpi qa-kpi--tone-accent">
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.COL_FRT') }}
              </div>
              <div class="qa-kpi-value">
                {{ frtPct(selectedDetail.first_human_response?.rate) }}
              </div>
              <div class="qa-kpi-sub">
                <template v-if="metricAvailable('first_human_response')">
                  {{
                    frtNum(
                      selectedDetail.first_human_response?.within_sla_count
                    )
                  }}
                  /
                  {{
                    frtNum(selectedDetail.first_human_response?.eligible_count)
                  }}
                  <template v-if="!rowScored(selectedDetail)">
                    ·
                    {{
                      t('QA_DASHBOARD.AGENT_KPI.SAMPLE_TOO_SMALL', {
                        count: rowSample(selectedDetail),
                        min: minSample,
                      })
                    }}
                  </template>
                </template>
                <template v-else>
                  {{ metricReasonText('first_human_response') }}
                </template>
              </div>
            </div>
            <div class="qa-kpi qa-kpi--tone-warn">
              <div class="qa-kpi-label">
                {{ t('QA_DASHBOARD.AGENT_KPI.ONGOING_OVERDUE') }}
              </div>
              <div class="qa-kpi-value qa-kpi-value--warn">
                {{ ongoingNum(selectedDetail.ongoing_response?.overdue_turns) }}
              </div>
              <div class="qa-kpi-sub">
                {{
                  t('QA_DASHBOARD.AGENT_KPI.KPI_UNANSWERED', {
                    count: ongoingNum(
                      selectedDetail.ongoing_response?.final_unanswered
                    ),
                  })
                }}
              </div>
            </div>
          </div>
          <p class="qa-desc">
            {{ t('QA_DASHBOARD.AGENT_KPI.ANSWER_PENDING_LLM') }}:
            {{
              num(
                selectedDetail.quality?.answer_accuracy?.pending_llm_suggestions
              )
            }}
          </p>
          <div class="qa-agent-kpi-links">
            <router-link
              class="qa-btn qa-btn--sm"
              :to="drillIncidents('template_audit')"
            >
              {{ t('QA_DASHBOARD.AGENT_KPI.LINK_TEMPLATE_INCIDENTS') }}
            </router-link>
            <router-link
              class="qa-btn qa-btn--sm"
              :to="drillIncidents('safety_language')"
            >
              {{ t('QA_DASHBOARD.AGENT_KPI.LINK_SAFETY_INCIDENTS') }}
            </router-link>
            <router-link class="qa-btn qa-btn--sm" :to="drillTemplateAudit()">
              {{ t('QA_DASHBOARD.AGENT_KPI.LINK_TEMPLATE_AUDIT') }}
            </router-link>
            <router-link class="qa-btn qa-btn--sm" :to="drillSafetyAudit()">
              {{ t('QA_DASHBOARD.AGENT_KPI.LINK_SAFETY_AUDIT') }}
            </router-link>
          </div>
          <p class="qa-desc">{{ t('QA_DASHBOARD.AGENT_KPI.DRILL_NOTE') }}</p>
        </div>
      </aside>
    </div>
  </div>
</template>

<style scoped>
.qa-kpi-sub {
  display: block;
  font-size: 11px;
  color: var(--qa-text-muted);
  font-weight: 400;
  margin-top: 2px;
}
.qa-kpi--muted {
  opacity: 0.72;
}
.qa-group-title {
  margin: 0 0 10px;
  font-size: 13px;
  font-weight: 600;
  color: var(--qa-text-secondary);
  display: flex;
  align-items: center;
  gap: 10px;
}
.qa-group-note {
  margin-left: auto;
  font-size: 11px;
  font-weight: 400;
  color: var(--qa-text-muted);
}
.qa-kpi-group {
  display: flex;
  flex-direction: column;
  gap: 0;
}
.qa-mono {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
}
.qa-agent-kpi-links {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin: 12px 0;
}
.qa-empty {
  text-align: center;
  color: var(--qa-text-muted);
  padding: 24px;
}
.qa-header-left .qa-desc {
  margin-top: 4px;
}
.qa-header-left .qa-pill {
  margin-top: 8px;
}
.qa-drawer-backdrop {
  position: fixed;
  inset: 0;
  z-index: 60;
  display: flex;
  justify-content: flex-end;
  background: rgba(15, 23, 42, 0.4);
}
.qa-drawer {
  width: min(560px, 100%);
  height: 100%;
  display: flex;
  flex-direction: column;
  background: var(--qa-surface, #ffffff);
  color: var(--qa-text, #111827);
  box-shadow: rgba(0, 0, 0, 0.28) -12px 0 32px -18px;
}
.qa-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 20px;
  border-bottom: 1px solid var(--qa-border, #e5e7eb);
}
.qa-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

/* Per-agent table: give columns room and un-cram the FRT cell so each column
   reads as its own clear thing (scoped — never touches the shared qaShell.css). */
.qa-table--rich th,
.qa-table--rich td {
  padding: 11px 18px;
  vertical-align: middle;
}

/* ── the per-agent table with a picked column set ── */
.qa-table--agents th,
.qa-table--agents td {
  white-space: nowrap;
}

/* The boxes carry their own padding, so the cell gives them only the gutter
   between columns — otherwise every figure sits inside two paddings and the
   row grows tall enough to fit three people on a screen. */
.qa-table--agents td {
  padding: 6px 8px;
}

/* The shared rich-table zebra fills even rows with --qa-muted, which is exactly
   the box fill, so every second row read as one merged band until a hover
   colour pulled the boxes apart again. Painting the CELL rather than fighting
   the stripe's selector: a td background always covers its tr, whatever the
   specificity, and the dark theme's own stripe rule outranks anything scoped
   here. The boxes are the row structure now, so the stripe has no work left. */
.qa-table--agents tbody td {
  background: var(--qa-surface);
}
.qa-table--agents tbody tr:hover td {
  background: var(--qa-row-hover, var(--qa-muted));
}
.qa-table--agents thead th {
  padding: 10px 12px;
}

/* The name stays put while the reader scrolls right, or by the eighth column
   they are reading numbers without knowing whose they are. The body cell takes
   its fill from the tbody rule below so it still answers to hover; only the
   header needs its own, having no such rule. */
.qa-table--agents .qa-col-sticky {
  position: sticky;
  left: 0;
  z-index: 2;
}
.qa-table--agents thead .qa-col-sticky {
  z-index: 3;
  background: var(--qa-muted);
}

.qa-cell-person-text {
  display: flex;
  flex-direction: column;
  line-height: 1.25;
}
.qa-cell-person-text small {
  font-size: 11px;
  color: var(--qa-text-muted);
}

/* Each figure sits in its own box, matching the Agent performance report so the
   two tables read as one system. The box also gives the tint somewhere to live
   that is not the whole table cell — a full-width wash reads as a broken row
   rather than a flagged figure. */
.qa-metric-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 2px;
  min-height: 52px;
  padding: 8px 10px;
  border-radius: 6px;
  /* Derived from the text colour rather than a fixed grey, so the same rule
     lifts the box off white on the light theme and off near-black on the dark
     one. --qa-muted was the obvious choice and the wrong one: it is also the
     zebra fill, so the boxes vanished on every second row. */
  background: color-mix(in srgb, var(--qa-text) 6%, var(--qa-surface));
}

/* Count large, share beneath it, movement last — the order the figures are
   trusted in. */
.qa-cell-count {
  font-size: 15px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}
.qa-cell-share {
  font-size: 11px;
  color: var(--qa-text-muted);
  font-variant-numeric: tabular-nums;
}
.qa-move {
  font-size: 11px;
  font-weight: 650;
  font-variant-numeric: tabular-nums;
}
.qa-move--bad {
  color: var(--qa-bad);
}
.qa-move--good {
  color: var(--qa-good);
}

/* Tints the box, not the text: the figure has to stay as readable as its
   neighbours, and the tint answers "where" rather than "how much". Mixed into
   the box's own fill rather than over transparency, so a tinted box keeps the
   same weight on the row as a plain one. */
.qa-cell-tone--bad {
  background: color-mix(in srgb, var(--qa-bad) 14%, var(--qa-surface));
}
.qa-cell-tone--warn {
  background: color-mix(in srgb, var(--qa-warn) 16%, var(--qa-surface));
}
.qa-cell-tone--good {
  background: color-mix(in srgb, var(--qa-good) 12%, var(--qa-surface));
}

.qa-chip--empty {
  background: transparent;
  color: var(--qa-text-muted);
}

/* ── column picker ── */
.qa-colpick {
  position: relative;
  margin-left: auto;
}
.qa-colpick-count {
  margin-left: 6px;
  padding: 1px 7px;
  border-radius: 999px;
  background: var(--qa-accent-soft);
  color: var(--qa-accent);
  font-size: 11px;
  font-variant-numeric: tabular-nums;
}
.qa-colpick-menu {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  z-index: 40;
  width: 300px;
  max-height: 60vh;
  overflow-y: auto;
  padding: 8px;
  background: var(--qa-surface);
  border: 1px solid var(--qa-border);
  border-radius: 12px;
  box-shadow: var(--qa-shadow);
}
.qa-colpick-group {
  padding: 8px 6px 4px;
}
.qa-colpick-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 0 4px 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--qa-text-muted);
}
.qa-colpick-opt {
  display: flex;
  align-items: center;
  gap: 9px;
  padding: 6px;
  border-radius: 7px;
  font-size: 13px;
  cursor: pointer;
}
.qa-colpick-opt:hover {
  background: var(--qa-muted);
}
.qa-colpick-opt--locked {
  cursor: default;
  opacity: 0.75;
}
.qa-colpick-opt small {
  margin-left: auto;
  font-size: 10px;
  color: var(--qa-text-muted);
}
.qa-colpick-foot {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  padding: 10px 10px 4px;
  margin-top: 4px;
  border-top: 1px solid var(--qa-border-soft);
}
.qa-linkbtn {
  font: inherit;
  font-size: 11.5px;
  font-weight: 600;
  background: none;
  border: 0;
  padding: 2px 4px;
  border-radius: 5px;
  color: var(--qa-accent);
  cursor: pointer;
}
.qa-table--rich td.qa-num {
  white-space: nowrap;
}
.qa-cell-headline {
  display: flex;
  align-items: baseline;
  gap: 8px;
  font-weight: 600;
}
.qa-cell-muted {
  color: var(--qa-text-muted);
  font-size: 12px;
}
.qa-table-header {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

/* This page alone runs the full width of the content area.
 *
 * qaShell.css caps every QA page at 1200 / 1400 / 1800px by viewport, and a
 * screen between the 1440 and 2560 breakpoints — a 4K panel at 175% scaling is
 * one — sits on the 1400px step with a few hundred pixels of empty page beside
 * it. On this page that is not just cosmetic: the per-agent table carries
 * fourteen columns and has to be scrolled sideways to be read, while the room
 * it needs is sitting unused to the right.
 *
 * Scoped on purpose. The other thirteen pages keep the cap — they carry far
 * less across, and full width would only spread them thinner. The scope
 * attribute lands on .qa-shell, so this outranks the shared rule on specificity
 * rather than on source order, and nothing about import order can undo it. */
.qa-page .qa-shell {
  max-width: none;
}
</style>
